import React, { createContext, useState, useContext, useEffect } from "react";
import { useRouter } from 'next/router';
import ReactGA from "react-ga4";
import CUError from "../pages/401_expired"
import { CardupdatrHelper } from "@/helpers/CardupdatrHelper";
import { Encryption } from "@strivve/strivve-sdk/lib/cardsavr/CardsavrSessionCrypto";
import { get_hash_key_values, merge_hash_key_values, isJSON } from "../common/utilities";
import { useIdleTimer } from "react-idle-timer";
import { Header } from "page-components/shared/Header";
import { ScrollToTop } from "page-components/shared/ScrollToTop";
import { hash_values } from "@/helpers/types";
import useLocalStorage from "hooks/useLocalStorage";
import { localStorageAvailable } from "@strivve/strivve-sdk/lib/cardsavr/CardsavrSessionUtilities";
import Handlebars from "handlebars"
import { CardsavrSession } from "@strivve/strivve-sdk/lib/cardsavr/CardsavrJSLibrary-2.0";
import { generateRandomPar, get_host_config } from "@strivve/strivve-sdk/lib/cardsavr/CardsavrSessionUtilities";
import CardsavrRestError from "@strivve/strivve-sdk/lib/cardsavr/CardsavrRestError";
import CardsavrSDKError from "@strivve/strivve-sdk/lib/cardsavr/CardsavrSDKError";

export const SessionContext = createContext({handleErrorConsole: null, abortAction: null, setAbortAction: null, setLoading: null, done_button_function : null, more_sites_button_function : null, error : null, addEventInfo : null, setCardId : null, setCardholderId : null, setIntegratorSession: null, preSelectedSites: null, update_card : null, update_address : null, agent_username : null, isIntegratorAuthenticated : false, create_cardholder : null, create_card : null, cardholder_cuid : null, integratorSession : null, loading : false, cardholder_id : null, app_configuration : null, get_merchant_sites : null, increment_page : null, submit_site_selection : null, currentSiteSelection : null, trace : null, form_fields : null, card_id : null, fi_lookup : null, onCredentialEntry : null, setOnCredentialEntry : null, sessionExpired : null, setSessionExpired : null, setSelectedSites : null, update_parent_site_state : null, submit_job : null, setShowHeader: null, login: null, ssoUser : null, isIdle : null, setIsIdle : null, setDoNotCloseMessage : null, doNotCloseMessage : null, setSessionNotification : null, sessionNotification : null, handleShowFinalTimeoutWarning : null, setError : null, queueNameOverride : null, setQueueNameOverride : null, cacheWriter : null, setCacheWriter : null, proxyPriorityOverride : null, setProxyPriorityOverride : null, type_override : null, setTypeOverride : null, setEnableDisabledSites : null, enableDisabledSites : null, setEnableUserCookies : null, enableUserCookies : null, overrideForm : null, setOverrideForm : null, get_job_types : null, activeTypeMenuItem : null, setActiveTypeMenuItem : null, activeCacheWriterMenuItem : null, setActiveCacheWriterMenuItem : null, queueNames : null, activeQueueNameMenuItem : null, setActiveQueueNameMenuItem: null, cardholder_safe_key: null, cardholderQuery: null, setDocument: null, pageOffset: 0});

interface create_cardholder_response {
    grant: string,
    id: number,
    cuid: string,
    cardholder_id: number
}

// eslint-disable-next-line react/prop-types
export const SessionProvider = ({ children }) => {  
    
    
    const router = useRouter();

    const [error, setError] = useState(null);
    const [integratorSession, setIntegratorSession] = useState(null);
    const [sessionExpired, setSessionExpired] = useState(false);
    const [integratorInformation, setIntegratorInformation] = useState(null);
    const [app_configuration, setAppConfiguration] = useLocalStorage("appConfiguration", null);
    const [loading, setLoading] = useState(true);
    const [cardholder_id, setCardholderId] = useLocalStorage("cardholder_id", null);
    const [cardholder_cuid, setCUID] = useLocalStorage("cardholder_cuid", null);
    const [cardholder_safe_key, setCardholderSafeKey] = useLocalStorage("cardholder_safe_key", "");
    const [selectedSites, setSelectedSites] = useState(null);
    const [hashKeyValues, setHashKeyValues] = useLocalStorage("hashKeyValues", null);
    const [showHeader, setShowHeader] = useState(false);
    const [trace, setTrace] = useState("");
    const [fi_lookup, setFILookup] = useState(null);
    const [agent_username, setAgentUsername] = useState(null);
    const [form_fields, setFormFields] = useState(null);
    const [card_id, setCardId] = useLocalStorage("card_id", null);
    const [cardholderQuery, setCardholderQuery] = useState(null);
    const [onCredentialEntry, setOnCredentialEntry] = useLocalStorage("onCredentialEntry", false);
    const [ssoUser, setSSOUser] = useLocalStorage("ssoUser", false);
    const [sessionNotification, setSessionNotification] = useState(null);
    const [isIdle, setIsIdle] = useState(false);
    const [doNotCloseMessage, setDoNotCloseMessage] = useState(false);
    const [overrideForm, setOverrideForm] = useState(false);
    const [enableDisabledSites, setEnableDisabledSites] = useState(false);
    const [enableUserCookies, setEnableUserCookies] = useState(undefined);
    const [activeTypeMenuItem, setActiveTypeMenuItem] = useState(null);
    const [activeQueueNameMenuItem, setActiveQueueNameMenuItem] = useState(null);
    const [activeCacheWriterMenuItem, setActiveCacheWriterMenuItem] = useState(null);
    const [queueNames, setQueueNames] = useState("");
    const [type_override, setTypeOverride] = useState("");
    const [queueNameOverride, setQueueNameOverride] = useState("");
    const [cacheWriter, setCacheWriter] = useState("");
    const [gaLoaded, setGaLoaded] = useState(false);
    const [proxyPriorityOverride, setProxyPriorityOverride] = useState("");
    const [pageOffset, setPageOffset] = useLocalStorage("pageOffset", 0);
    const [clickstream, setClickstream] = useLocalStorage("clickstream", []);
    const [source, setSource] = useLocalStorage("source", null);
    const [preSelectedSites, setPreSelectedSites] = useState([]);
    const [abortAction, setAbortAction] = useState("");

    useEffect(() => {

        const load = async() => {
            setLoading(() => true);

            // Configuration overrides from hash
            const hash_key_values: hash_values = { ...hashKeyValues, ...transform_hash_keys(get_hash_key_values()) }; 
            //for SSO users
            const isSSO = !!hash_key_values.grant;
            if (isSSO) {
                resetCardUpdatrSession();
                if (cardholder_cuid) { // this means there's an old cuid in local storage
                    return;
                }
            }
            
            const { session, app_configuration, integrator, host_configuration, sso_cardholder_id, selected_sites } = await initial_configuration(hash_key_values, isSSO);
            setIntegratorInformation(() => ({...integrator}));
            setAppConfiguration(() => ({...app_configuration}));
            setFILookup(() => host_configuration.fi_lookup);
            
            if(integrator && host_configuration.fi_lookup && app_configuration) {
                try {
                    // The following block is to check if a cardholder_cuid already exists in session storage. If it does, the page has likely been reloaded. 
                    if(cardholder_cuid) {
                       const refresh_result = await CardupdatrHelper.getInstance().refresh_user_session(integrator.username);
                        // if the refresh body does not include the cardholder_id that was authorized via the passed in grant, the current session is invalid.
                        if(!refresh_result.body.authorized_cardholders.includes(cardholder_id)){
                            // if its an sso user, bring up error page. Otherwise, clear storage and reload the page.
                            if(app_configuration.grant && app_configuration.card_id) {
                                //if sso cardholder id is in authed cardholder array then we are good
                                if(!refresh_result.body.authorized_cardholders.includes(sso_cardholder_id)){
                                    setError({ title: "Could not find grant authorization.", statusCode: 500 })
                                }
                            } else { // in the case of reloading a non-sso user that we can no longer act on behalf on.
                                console.log(`Could not find grant (value: ${app_configuration.grant}) and/or card_id (value: ${app_configuration.card_id}) for this cardholder: ${cardholder_id}. `);
                                setError({ statusCode: 401 });
                            }
                        }
                    }
                } catch(e) {
                    setError({ title: "Failed to initialize session", statusCode: 401 });
                    console.log("Failed to init session", e);
                    setLoading(false);
                }
            } else {
                console.log("Missing a required property for initialization. ", [integrator, host_configuration.fi_lookup, app_configuration]);
                setError({ title: "Failed to initialize session", statusCode: 500 });
            }
    
            if(localStorageAvailable()) {
                const sites = window.sessionStorage.getItem("selectedSites");
                //sites need to set so that downstream useEffect hooks can be called; however, they should be cleared out if this is an ssoUser
                if(((ssoUser && !app_configuration.grant) || (!ssoUser)) && sites){
                    setSelectedSites(JSON.parse(sites));
                }
                else{
                    setSelectedSites([]);
                }
            }
            setLoading(() => false);
            window.parent.postMessage(["init", ``], "*");
            if(selected_sites){
                let sites = await session.get(`/merchant_sites`, {ids:selected_sites});
                sites = sites.body.map((site) => ({ ...site, state: "NOT_SUBMITTED" }))
                setPreSelectedSites(sites);
            }
    
            setLoading(false);
   
        }
        load();
                
    }, [cardholder_cuid]);

    function handleErrorConsole(err) {
    
        if (err instanceof CardsavrRestError || (err.type && err.type === "CardsavrRestError")) {
            if (err.errors) {
                console.log("Errors returned from REST API : " + err.response.call);
                err = err.response;
                err.body._errors.map((obj) => {
                    console.log(obj);
                });
                return err.body._errors
            } else if (err.response.body) {
                console.log(err.response);
                console.log("Message returned from REST API: " + err.response.body.message);
            }
        } else if (err instanceof CardsavrSDKError || (err.type && err.type === "CardsavrSDKError")) {
            console.log("SDK errors, exception stack below:");
            console.log(err.errors);
            console.log(err.stack);
        } else {
            console.log("no errors from REST API, full error below:");
            console.log(err);
        }
    
        if(err.statusCode === 401){
            router.push("/login");
        }
        else if(err.statusCode === 403 && err.response.body._errors[0].name === "Password Update Required") {
                router.push('/reset_password');
        }
    }

    function addEventInfo(url : string, page_title : string){
        const new_clickstream = [ ...clickstream, { url, timestamp : new Date().toISOString(), page_title }];
        setClickstream(new_clickstream);
        if (cardholder_cuid && cardholder_id) {
            const ch = CardupdatrHelper.getInstance();
            const session = ch.getSession(agent_username);
            session.updateCardholderSession(null, { clickstream : new_clickstream, cuid : cardholder_cuid });
        }
    }

    function setDocument(url : string, page_title : string, fi_name : string) {
        setPageOffset(app_configuration.page_order.indexOf(url));
        addEventInfo(url, page_title);   
        
        // If we are showing the header, let's also set the title
        if (app_configuration.show_header && (!document.title || document.title.startsWith(fi_name))) {
            document.title = `${fi_name} - ${page_title}`;
            window.parent.postMessage(["setTitle", document.title], "*");
        }
        if (app_configuration.ga_measurement_id) {
            if((Array.isArray(app_configuration.ga_measurement_id) && app_configuration.ga_measurement_id.length > 0)){
                if (!gaLoaded) {
                    const ga_arr = (Array.isArray(app_configuration.ga_measurement_id) ? 
                        app_configuration.ga_measurement_id : 
                        [app_configuration.ga_measurement_id]).map((item: string) => ({ trackingId: item, gaOptions : { cookieFlags: "secure;samesite=none" } }));
                    ReactGA.initialize(ga_arr );
                    setGaLoaded(true);
                }
                ReactGA.send({ hitType: "pageview", page: url, page_title });
            }
        }
    }

    function transform_hash_keys(hashKeysOld) {
        const hashKeys = {...hashKeysOld};

        // Map header_background_color to hex_color
        if(typeof hashKeys.header_background_color !== "undefined") {
            hashKeys.hex_color = hashKeys.header_background_color;
        }

        // Custom Data for Users
        if(typeof hashKeys.custom_data !== "undefined" && isJSON(hashKeys.custom_data)) {
            hashKeys.custom_data = JSON.parse(hashKeys.custom_data);
        }

        // Name(?)
        if(typeof hashKeys.name !== "undefined") {
            hashKeys.institution_name = hashKeys.name;
        }

        // Countries Supported (check if Object assign pulled from hash values as string, or if information_object has it)
        if(typeof hashKeys.countries_supported !== "undefined") {
            hashKeys.countries_supported = isJSON(hashKeys.countries_supported) 
                ? JSON.parse(hashKeys.countries_supported) 
                : hashKeys.countries_supported = hashKeys.countries_supported.split(",");
        }

        // Merchant site tags
        if(typeof hashKeys.merchant_site_tags !== "undefined" && isJSON(hashKeys.merchant_site_tags)) {
            hashKeys.merchant_site_tags = JSON.parse(hashKeys.merchant_site_tags);
        }
    
        // Top Sites
        if(typeof hashKeys.top_sites !== "undefined" && isJSON(hashKeys.top_sites)) {
            hashKeys.top_sites = JSON.parse(hashKeys.top_sites);
        }

        return hashKeys;
    }

    useEffect(() => {
        if(localStorageAvailable() && selectedSites !== null) {
            window.sessionStorage.setItem("selectedSites", JSON.stringify(selectedSites));
        }
    }, [selectedSites])

    const get_fi_configuration = async(username, fi_lookup) => {
        try {
            const fi = await CardupdatrHelper.getInstance().get_fi(username, fi_lookup);
            if(fi && fi.length > 0) {
                return {...fi[0].config, institution_name: fi[0].name, financial_institution_id: fi[0].id};
            } else {
                return false;
            }
        } catch(e) {
            console.log("eating the error", e);
            return false;
        }
    }

    const initial_configuration = async (hash_key_values, isSSO) => {
        
        const res = await fetch("/config.json");
        let config = await res.json();
        
        // New style has encrypted key / body in an array.  If it's not an array, it will work as it used to.  
        if (Array.isArray(config)) {
            config = await Encryption.decryptResponse(config[0], {"encrypted_body" : config[1]});
        }
        const {username, password, name, key, api_port, api_instance, queue_names, type_override, queue_name_override} = config; // Integrator Information
        setAgentUsername(() => username); 
        const host_config = get_host_config(hash_key_values.financial_institution, api_port, api_instance);

        if (type_override) {
            setTypeOverride(type_override);
        }
        if (queue_name_override) {
            setQueueNameOverride(queue_name_override);
        }
        const newstr = queue_names.split(",");
        setQueueNames(newstr);

        const new_source = JSON.parse(hash_key_values.source ?? "{}");
        if (!source) {
            new_source.integration = `CU2${isSSO ? "_SSO" : ""}`;
            if (!new_source.device) {
                new_source.device = window.navigator.maxTouchPoints > 0 && new RegExp("/Android/iPhone/i").test(window.navigator.userAgent) ? "mobile web" : "desktop web";
            }
            setSource(() => new_source);
        }

        // Set up the CardupdatrHelper
        const ch = CardupdatrHelper.getInstance();
        ch.setAppSettings(host_config.api_url, name, key, true, null, null, false);
        let cardholder_rest_response, session: CardsavrSession;
        let sso_cardholder_id, selected_sites, fi_configuration;

        try {
            session = await login(username, password);
            fi_configuration = await get_fi_configuration(username, host_config.fi_lookup);
            if (isSSO) {
                cardholder_rest_response = await session.authorizeCardholder(hash_key_values.grant, source ?? new_source);
                if (fi_configuration.financial_institution_id !== cardholder_rest_response.body.cardholder?.financial_institution_id) {
                    //setError(() => ({ statusCode: 404, title: "Cardholder not valid within " + fi_configuration.institution_name}));
                }
                selected_sites = hash_key_values.selected_sites ? hash_key_values.selected_sites : null;
                setCardId(+hash_key_values.card_id);
                const cuid = cardholder_rest_response.body.cardholder.cuid;
                const cardholder_safe_key = cardholder_rest_response.body.cardholder_safe_key;
                session.setTrace(agent_username, {key : cuid});
                setCardholderSafeKey(cardholder_safe_key ?? "");
                sso_cardholder_id = cardholder_rest_response.body.cardholder.id;
                setCardholderId(sso_cardholder_id);
                setCardholderQuery(() => ch.createCardholderQuery(username, sso_cardholder_id));
                setTrace(cuid);
                setSSOUser(true);
                delete hash_key_values.grant;
                delete hash_key_values.card_id;
                setHashKeyValues(hash_key_values);
                setCUID(cuid);
                window.location.hash = merge_hash_key_values(hash_key_values); // Strip the grant and card id from the url.
            }

            // hashKayValues needs to have the guid and card id removed
            if (!hashKeyValues) {
                setHashKeyValues(() => hash_key_values);
            }

            if(!fi_configuration || (fi_configuration.config && fi_configuration.config.disable_brand)) { // get_fi_configuration returns false if there are no FI's in the body response.
                setError(() => ({ statusCode: 404, title: "Brand not supported" }));
            }
        } catch(e) {
            console.error(e);
            setError({ title: "Failed to initialize session", statusCode: 401 });
            setLoading(false);
            setCardholderId(null);
            setCUID(null);
        }

        const default_configuration = {
            drop_shadow : false,
            show_header : false,
            dynamic_height : false,
            merchant_site_tags : ["usa", "prod"],
            countries_supported : ["USA"],
            top_sites : ["amazon.com", "apple.com", "audible.com", "hulu.com", "netflix.com", "spotify.com", "target.com", "uber.com", "venmo.com", "walgreens.com", "walmart.com"],
            header_background_color : "black",
            link_color : "black",
            button_color : "black", 
            border_color : "black", 
            bin_ranges : [],
            page_order : ["/select-merchants", "/user-data-collection", "/credential-entry"],
            merchant_selection_message : `Select the sites below to make your {{{card_description}}} the primary payment method.`,
            final_message : "Your current card placements require no further interaction. Click 'More sites' to place your card at additional sites.",
            financial_institution : host_config.fi_lookup,
            close_url : "close",
            more_sites_url : "/select-merchants"
        }

        //if there is a pre-existing app_configuration in the
        const merged_config = { institution_name : fi_configuration.institution_name, ...default_configuration, ...fi_configuration.style_template, ...fi_configuration.config, ...hash_key_values };
        Object.keys(merged_config).map(key => {
            // since a lot of these values come from a query string, they are case to a string, when portal values and defaults are booleans
            merged_config[key] = merged_config[key] === "null" ? null : merged_config[key];
            merged_config[key] = merged_config[key] === "false" ? false : merged_config[key];
            merged_config[key] = merged_config[key] === "true" ? true : merged_config[key];
        });

        if(merged_config.favicon_url && merged_config.favicon_url !== "null") {
            // Favicon
            const link_node = document.createElement("link");
            link_node.type = "image/x-icon";
            link_node.rel = "shortcut icon";
            link_node.href = merged_config.favicon_url;
            window.parent.postMessage(["setFavicon", `${merged_config.favicon_url}`], "*");
            document.head.appendChild(link_node);
        }

        merged_config.card_description = merged_config.card_description ?? `${merged_config.institution_name} card`;

        merged_config.merchant_selection_message = 
            Handlebars.compile(merged_config.merchant_selection_message)(merged_config);

        if (!merged_config.overlay && merged_config.close_url === default_configuration.close_url) {
            merged_config.close_url = "none";
            console.error("close_url must be configured as 'none', '/select-merchants',  or a third_party url if not running as an overlay.");
            //setError({title : "close_url can only be 'close', 'none' or a third party url when running an overlay.", statusCode: 401})
        }

        if(isSSO) {
            // for sso, done button can't start with a /.
            if (merged_config.close_url.startsWith("/")){
                setError({title : "close_url should be 'close', 'none' or a third party url", statusCode: 401})
            }
        }
        if(preSelectedSites?.length > 0){
            if (merged_config.more_sites_url.startsWith("/")) {
                setError({title : "more_sites_url must be configured with a third party url for cardupdatr with pre-selected sites.", statusCode: 401});
            } else if (merged_config.close_url.startsWith("/")) {
                setError({title : "close_url must be configured with 'none', 'close' or a third party url for cardupdatr with pre-selected sites.", statusCode: 401});
            }
        }

        if(merged_config.page_title) { // this seems odd
            merged_config.institution_name = merged_config.page_title;
        }
        
        // /**  
        //  * If we are running in a non-local environment, we want to strip out all logging.   
        //  */  
        function noop() {}  
        if(window.location.host.indexOf("localhost.") !== 0) {  
            console.log = noop; 
            console.warn = noop;    
        }

        if(merged_config.web_fonts){
            merged_config.web_fonts.map(async (font) => {
                const font_to_load = new FontFace(`${font.name}`, `url(${font.url})`, {"weight":font.weight ? font.weight : "normal"});
                await font_to_load.load();
                document.fonts.add(font_to_load);
            })
        }

        if(merged_config.font_family){
            document.body.style.fontFamily = merged_config.font_family;
        }

        return { selected_sites, session, integrator : config, host_configuration : host_config, app_configuration : merged_config, sso_cardholder_id}
    }

    const login = async (username : string, password : string, cardholderId : string = cardholder_id) => {
        const ch = CardupdatrHelper.getInstance();
        const integrator_session = await ch.loginAndCreateSession(username ?? integratorInformation.username, password ?? integratorInformation.password);
        setIntegratorSession(() => integrator_session);
        if (cardholderId) {
            setCardholderQuery(() => ch.createCardholderQuery(username, cardholder_id));
        }
        return integrator_session;
    };

    const increment_page = (offset? : number) : string => {
        const newPO = offset === undefined ? pageOffset + 1 : offset;
        setPageOffset(newPO);
        return app_configuration.page_order[newPO];
    }

    const get_merchant_sites = async () => {
        try {
            const tags = app_configuration.merchant_site_tags ?? "";
            const top_sites = app_configuration.top_sites ? app_configuration.top_sites.toString() : null;
            const exclude_hosts = app_configuration.exclude_sites ? app_configuration.exclude_sites.toString() : null;
            const merchant_sites = await CardupdatrHelper.getInstance().get_merchant_sites(agent_username, top_sites, tags, exclude_hosts)
            
            const sites_with_state = merchant_sites.map((site) => ({ ...site, state: "NOT_SUBMITTED" }));
            return sites_with_state;
        } catch(e) {
            // If we fail, there was (probably) an issue with the session.
            if(ssoUser) { // if its an sso user, we could have a number of issues (bad grant, expired grant), since the user doesn't care, just tell them the sites coulnd't be loaded.
                console.log("Failed to retrieve merchant sites. Likely a problem with the grant usage.", e);
                setError({ title: "Unable to load sites", statusCode: 401 });
            } else { // For a normal CU user, we will clear the session items and reload the parent iframe.
                console.log("Failed to retrieve merchant sites. This should not have happened (see API logs if client error is not clear)", e);
                setError({ statusCode: 401 });
            }
        }
    }

    const submit_job = async (site) => {
        var abort_action_to_submit;
        if(abortAction=="getFromSite"){
            abort_action_to_submit = site.testing_abort_action;
        }
        else {
            abort_action_to_submit = abortAction;
        }
        const cache_writer_active = cacheWriter === "Write";
        const cache_reader_active = cacheWriter === "Read";
        const user_cookie_reader_writer_enabled = {
            active : !!enableUserCookies
        };
        const user_cookie_reader_writer = typeof enableUserCookies === "boolean" ? user_cookie_reader_writer_enabled : undefined;
        const vbs_config : any = {
            chrome_config : {},
            job_config : {
                abortAction : abort_action_to_submit,
                user_data_settings:{
                    cache_writer : {
                        "active" : cache_writer_active
                    },
                    cache_reader : {
                        "active" : cache_reader_active
                    },
                    user_cookie_reader_writer
                }
            }
        }
        const job = await CardupdatrHelper.getInstance().submit_user_job(agent_username, cardholder_id, site, CardupdatrHelper, card_id, queueNameOverride, type_override, proxyPriorityOverride, cardholder_cuid, cardholder_safe_key, vbs_config);
        const submitted_site = {...site, job_id : job.id, account_id : job.account_id, state : "SUBMITTED", has_creds : !!job.account.last_login}
        setSelectedSites(selectedSites.map(site => submitted_site.id === site.id ? submitted_site : site));
        return submitted_site;
    }
    // Submits the site selection to state.
    const submit_site_selection = async (site_selection) => {
        if(cardholder_cuid && card_id) {
            setLoading(true);
            if (app_configuration.grant) {
                //for sso users: wipe out grant, so that we will be able to distinguish if an sso user is going back to select more sites 
                setAppConfiguration(() => ({...app_configuration, grant : ""}));
            }
        }
        // grab accounts and assign to each submitted site
        const ch = CardupdatrHelper.getInstance();
        const agent_session = ch.getSession(agent_username);
        const accounts = cardholder_id ? (await agent_session.getAccounts({cardholder_ids : [cardholder_id]}))?.body : [];
        const sites_to_submit = site_selection.map(site => {
            return { ...site, account_id : (accounts.find(account => account.merchant_site_id == site.id))?.id };
        });

        setSelectedSites(sites_to_submit);
        const target_form_fields = [];
        const form_fields = site_selection.reduce((accumulator, site) => Object.assign(accumulator, site.required_form_fields), target_form_fields);
        setFormFields(() => [...form_fields]);
        setLoading(false);
    }

    const create_cardholder = async (cardholder_information, financial_institution) => {
        if(app_configuration.custom_data) {
            cardholder_information.custom_data = app_configuration.custom_data;
        }

        const ch = CardupdatrHelper.getInstance();
        const cardholder = await ch.create_cardholder(agent_username, financial_institution, cardholder_information, source) as create_cardholder_response;
        setCardholderId(() => cardholder.id);
        setCUID(() => cardholder.cuid);
        setTrace(() => cardholder.cuid);
        setCardholderQuery(() => ch.createCardholderQuery(agent_username, cardholder.id));
        const agent_session = ch.getSession(agent_username);
        agent_session.setTrace(agent_username, {key : cardholder.cuid});

        return cardholder;
    }

    const create_card = async (card_info, address_info, cardholder) => {
        const ch = CardupdatrHelper.getInstance();
        const agent_session = ch.getSession(agent_username);
        card_info.par = generateRandomPar(card_info.pan, card_info.expiration_month, card_info.expiration_year, cardholder.cuid + card_info.address_id);
        address_info.cardholder_id = card_info.cardholder_id = cardholder.id; // address needs cardholder_id ref
        card_info.address = address_info; // 
        // fiserv hack testing
        const card_response = await agent_session.createCard(card_info, cardholder_safe_key, {"x-cardsavr-hydration" : JSON.stringify(["address"]) });
        setCardId(() => card_response.body.id);
        if(!card_response.body.bin_id && (app_configuration.bin_ranges.length === 0 || !app_configuration.bin_ranges)) {
            return setError({ title: `Null Bin ID Error: No Bin Range Specified`, statusCode: 401 });
        }
        return card_response.body.id;
    }

    const update_address = async (body) => {
        const ch = CardupdatrHelper.getInstance();
        const agent_session = ch.getSession(agent_username);
        const cards = (await agent_session.getCards({ids : card_id})).body;
        return await agent_session.updateAddress(parseInt(cards[0].address_id), body);
    }

    const update_card = async (body) => {
        const ch = CardupdatrHelper.getInstance();
        const agent_session = ch.getSession(agent_username);
        return await agent_session.updateCard(parseInt(card_id), body, cardholder_safe_key);
    }

    // site "state" is a terrible name, it's a CardUpdatr specific status of the job (not to be confused with job status - although they are similar
    function update_parent_site_state(hostname, state): void {
        if(state !== "NOT_SUBMITTED") {
            const site = selectedSites.find(site => site.host === hostname);
            if (site && site.state !== state) {
                const new_sites = selectedSites.map(site => site.host === hostname ? { ...site, state, has_creds : true } : site);
                setSelectedSites(() => new_sites);
            }
        } else if(state === "NOT_SUBMITTED") {
            setSelectedSites(selectedSites => selectedSites.map(site => site.host === hostname ? { ...site, job_id: null, account_id: null, selected: false, state } : site));
        }
    }

    const get_job_types = async () => {
        const ch = CardupdatrHelper.getInstance();
        try{
            const session = ch.getSession(agent_username);
            const schema = await session.get("/place_card_on_single_site_jobs/schema", {});
            const type_properties = schema.body.properties.type_override.options;
            return type_properties;
        }
        catch(err){
            console.log("Could not fetch job types.")
            return ['RPA:CARD_PLACEMENT', 'RPA_LOOPBACK:CARD_PLACEMENT', 'DCS_LOOPBACK:CARD_PLACEMENT'];
        }
    };

    const handleOnIdle = async () => {
        console.log("setting user idle. two min!");
        setIsIdle(true);
        setSessionNotification("session_warning");
    };

    const handleOnActive = async () => {
        if(!sessionExpired) {
            if(sessionNotification === "session_warning"){
                setSessionNotification("");
            }
            console.log("setting user active");
            setIsIdle(false);
            try {
                window.parent.postMessage(["session_refresh", ""], "*");
                await CardupdatrHelper.getInstance().refresh_user_session(agent_username);
            } catch(e) {
                console.log(e);
                setSessionExpired(true);
                setDoNotCloseMessage(false);
            }
        }
    };

    useIdleTimer({
        timeout : 1000 * 60 * 13,
        onIdle : handleOnIdle,
        onAction : handleOnActive,
        eventsThrottle : 500,
        throttle : 60000
    });
    
    const handleShowFinalTimeoutWarning = async (): Promise<void> => {
        if(isIdle) {
            console.log("User has not interacted with the page after the two minute warning.");
            setError({ title: `Session expired.`, statusCode: 401 });
            setSessionExpired(true);
            setDoNotCloseMessage(false);
            setIsIdle(false);
        } else {
            setSessionNotification(null);
            await CardupdatrHelper.getInstance().refresh_user_session(agent_username);
        }
    };

    function resetCardUpdatrSession() {
        const tmpHashKeyValues = { ...hashKeyValues };  //back up hash keys, because we need them.
        const ch = CardupdatrHelper.getInstance();
        cardholderQuery?.removeAll(); // stop polling
        setCardholderQuery(null);
        try {
            setIntegratorSession(null); 
            ch.endSession(agent_username);  
        } catch (err) {
            console.log("can't end session because session doesn't exist, that's ok if we didn't start with one.");
        }
        setSelectedSites([]); 
        setCardId(null);
        setCardholderId(null);
        if(localStorageAvailable() && selectedSites !== null) {
            window.sessionStorage.clear();
        }
        setHashKeyValues(tmpHashKeyValues);  // put back the hash keys
        setCUID(null);
    }

    function getButtonNavigationFunction(end_session : boolean, url : string) {
        if (!url || url === "none") {
            return null;
        }
        return async () => {
            setSessionNotification("");
            setError(false);
            if (end_session) {
                resetCardUpdatrSession();
            }
            if (url.startsWith("/")) {
                router.push(url);
            } else if (url === "close") {
                window.parent.postMessage(["close_overlay", ""], "*")
                window.close();
            } else {
                window.parent.postMessage(["done", url], "*")
            }
        };
    }
    const done_button_function = getButtonNavigationFunction(true, app_configuration?.close_url);
    const more_sites_button_function = getButtonNavigationFunction(false, app_configuration?.more_sites_url);

    return (
        <>
            {app_configuration?.overlay && (app_configuration.show_close_overlay_button !== false) ? <p onClick={getButtonNavigationFunction(true, "close")} style={{"color": "#626262", "right": "5px", "cursor": "pointer", "position": "fixed", "top": "0px", "fontSize": "14px", "zIndex": "999"}}>✕</p> : null}

            {error ? (
                // Render the error component only if there is an error
                <CUError style={{borderColor: app_configuration?.button_color, backgroundColor: app_configuration?.button_color, borderRadius: app_configuration?.button_border_radius, padding: app_configuration?.button_padding}} buttonText={error.buttonText} statusCode={error.statusCode} title={error.title} ssoUser={ssoUser} linkFunction={done_button_function} />
            ) : (
                // Render the SessionContext.Provider if there is no error
                <SessionContext.Provider value={{ handleErrorConsole, abortAction, setAbortAction, setLoading, done_button_function, more_sites_button_function, error, addEventInfo, setCardId, setCardholderId, setIntegratorSession, preSelectedSites, update_card, update_address, agent_username, isIntegratorAuthenticated : !!integratorSession, loading, cardholder_cuid, cardholder_id, create_cardholder, create_card, submit_job, login, integratorSession, app_configuration, get_merchant_sites, increment_page, submit_site_selection, currentSiteSelection : selectedSites, trace, form_fields, card_id, fi_lookup, onCredentialEntry, setOnCredentialEntry, sessionExpired, setSessionExpired, setSelectedSites, update_parent_site_state, setShowHeader, ssoUser, isIdle, setIsIdle, doNotCloseMessage, sessionNotification, handleShowFinalTimeoutWarning, setDoNotCloseMessage, setSessionNotification, setError, setEnableDisabledSites, enableDisabledSites, setEnableUserCookies, enableUserCookies, overrideForm, setOverrideForm, get_job_types, activeTypeMenuItem, setActiveTypeMenuItem, activeQueueNameMenuItem, setActiveQueueNameMenuItem, queueNames, queueNameOverride, setQueueNameOverride, cacheWriter, setCacheWriter, activeCacheWriterMenuItem, setActiveCacheWriterMenuItem, proxyPriorityOverride, setProxyPriorityOverride, type_override, setTypeOverride, cardholder_safe_key, cardholderQuery, setDocument, pageOffset}}>
                {app_configuration?.show_header && showHeader ? <Header /> : <div style={{ height: "5px" }}></div>}
                    {children}
                    <ScrollToTop />
                </SessionContext.Provider>
            )}
        </>
    );
};

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export const useAuth = () => useContext(SessionContext);
